/**
 * Business-language milestones for the customer run history (RunJourney).
 * Doubles as `eventsToJourney`'s default pack, so pure-module tests stay
 * stable. Kept in sync with the stage language in `beats.ts` by review, not
 * by code — the two describe the same moments at different zoom levels.
 */

import type { JourneyStrings } from '../../../components/journey'

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`
}

export const journey = {
  runStarted: 'Run started',
  reviewedRequest: 'Reviewed your request',
  gatheredAdditionalMaterials: 'Gathered additional materials',
  attachmentsChip: (n: number) => plural(n, 'attachment'),
  referenceDocumentsChip: (n: number) => plural(n, 'reference document'),
  working: 'Working',
  workAnalysis: 'analysis',
  workDrafting: 'drafting',
  workHandsOnTasks: (n: number) => plural(n, 'hands-on task'),
  workOutsideLookups: (n: number) => plural(n, 'outside lookup'),
  workNotesUpdated: 'notes updated',
  specialistTeamAtWork: 'Specialist team at work',
  specialistTeamWorking: (n: number) => `Specialist team at work — ${plural(n, 'specialist')}`,
  specialistTeamDone: (n: number) => `Specialist team — ${plural(n, 'specialist')}`,
  specialistProgress: (done: number, total: number, failed: number) =>
    `${done} of ${total} finished${failed > 0 ? ` · ${failed} had trouble` : ''}`,
  moreChip: (n: number) => `+${n} more`,
  askedYouAQuestion: 'Asked you a question',
  askedYouAQuestionRound: (round: number) => `Asked you a question (round ${round})`,
  noReplyInTime: 'No reply in time — continued with documented assumptions',
  cancelledByYou: 'Cancelled by you',
  youReplied: 'You replied',
  hitASnag: 'Hit a snag — recovered automatically',
  hitASnagTimes: (n: number) => `Hit a snag — recovered automatically (×${n})`,
  packagedDeliverable: 'Packaged the deliverable',
  filesChip: (n: number) => plural(n, 'file'),
  delivered: 'Delivered',
  deliveredWithNotes: 'Delivered — with notes',
  runFailedTitle: 'The run hit a problem and stopped',
  runFailedDetail: 'Our team can see the details on their side — you can also try starting a new run.'
} satisfies JourneyStrings
